import { React, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { Form, Space, Table, Popconfirm, Tooltip } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  EuroCircleOutlined,
} from "@ant-design/icons";
import {
  appTitle,
  Months,
  MonthsPortuguese,
} from "../common/GeneralConfigurations";
import { AppRoutes } from "../../AppRoutes";
import {
  getEmployeeRecommendationBenefits,
  updateEmployeeRecommendationBenefit,
} from "../../api/EmployeeRecommendationBenefitsApi";
import AdnMessageError from "../common/adnMessage/AdnMessageError";

import EmployeeAwardsListForm from "./EmployeeAwardsListForm";
import { getEmployees } from "../../api/EmployeeApi";

const ManageEmployeeAwardsListPage = () => {
  //CONSTS

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const title = t("employeeAwards.title");

  const months = Object.entries(Months).map((m) => ({ id: m[0], name: m[1] }));
  const yearArray = [];
  for (let y = 2020; y <= new Date().getFullYear() + 1; y++) {
    yearArray.push(y);
  }
  const years = Object.entries(yearArray).map((y) => ({
    id: y[1],
    name: y[1],
  }));

  //STATES

  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [employeeList, setEmployeeList] = useState([]);
  const [referencedByList, setReferencedByList] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [orderBy, setOrderBy] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [referencedByName, setReferencedByName] = useState("");
  const [payment, setPayment] = useState("");
  const [benefitStatus, setBenefitStatus] = useState(0);
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [refetch, setRefetch] = useState(false);

  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [recommendationBenefit, setRecommendationBenefit] = useState({});

  //TABLE COLUMNS

  const FirstLastName = (name) => {
    let fullName = name.split(" ");
    let firstName = fullName[0];
    return firstName.concat(" " + fullName[fullName.length - 1]);
  };

  const columns = [
    {
      title: t("employeeAwards.columns.employeeName"),
      dataIndex: "employeeName",
      width: "25%",
      sorter: true,
      render: (text, record) => (
        <Link to={AppRoutes.consultantById.createPath(record.employeeId)}>
          {FirstLastName(record.employeeName)}
        </Link>
      ),
    },
    {
      title: t("employeeAwards.columns.referencedByName"),
      dataIndex: "referencedByName",
      sorter: true,
      width: "25%",
    },
    {
      title: t("employeeAwards.columns.dueDate"),
      dataIndex: "dueDate",
      sorter: true,
      //width: "20%",
      render: (text) =>
        text
          ? MonthsPortuguese[parseInt(moment(text).format("MM"))] +
            " " +
            moment(text).format("YYYY")
          : undefined,
    },
    {
      title: t("employeeAwards.columns.payment"),
      dataIndex: "payment",
      //width: "15%",
      render: (text, record) =>
        record.payment == 1
          ? t("employeeAwards.payment.firstPayment")
          : record.payment == 2
          ? t("employeeAwards.payment.secondPayment")
          : "???????",
    },
    {
      title: t("employeeAwards.columns.paymentStatus"),
      dataIndex: "status",
      //width: "15%",
      render: (text, record) =>
        record.status == 0 ? (
          <div className="state-tag tag-danger">
            {t("employeeAwards.status.notPaid")}
          </div>
        ) : record.status == 1 ? (
          <div className="state-tag tag-done">
            {t("employeeAwards.status.paid")}
          </div>
        ) : (
          <div className="state-tag tag-canceled">
            {t("employeeAwards.status.canceled")}
          </div>
        ),
    },
    //Table.EXPAND_COLUMN,
    //{
    //  title: "Notes",
    //  dataIndex: "notes",
    //  width: "15%",
    //  ellipsis: true,
    //},
    {
      title: t("employeeAwards.columns.action"),
      key: "action",
      ellipsis: true,
      render: (record) => (
        <Space style={{ display: "flex", justifyContent: "center" }}>
          <Tooltip title={t("employeeAwards.tooltips.editNotes")}>
            <EditOutlined
              onClick={() => {
                form.setFieldsValue(record);
                setInitValues(record);
                setEditModalVisible(true);
              }}
            />
          </Tooltip>
          {record.status == 0 ? (
            <>
              <Popconfirm
                title={t("employeeAwards.confirmations.payBenefit")}
                onConfirm={() => {
                  handlePayBenefit(record);
                }}
              >
                <Tooltip title={t("employeeAwards.tooltips.payBenefit")}>
                  <EuroCircleOutlined style={{ color: "green" }} />
                </Tooltip>
              </Popconfirm>

              <Tooltip title={t("employeeAwards.tooltips.cancelPayment")}>
                <CloseOutlined
                  style={{ color: "red" }}
                  onClick={() => {
                    form.setFieldsValue(record);
                    setInitValues(record);
                    setCancelModalVisible(true);
                  }}
                />
              </Tooltip>
            </>
          ) : record.status == 2 ? (
            <Popconfirm
              title={t("employeeAwards.confirmations.reactivate")}
              onConfirm={() => {
                handleUnCancelFinish(record);
              }}
            >
              <Tooltip title={t("employeeAwards.tooltips.reactivatePayment")}>
                <CheckOutlined style={{ color: "green" }} />
              </Tooltip>
            </Popconfirm>
          ) : (
            <></>
          )}
        </Space>
      ),
      width: "10%",
    },
  ];

  //USEEFFECTS

  useEffect(() => {
    document.title = `${appTitle} - ${title}`;
  }, []);

  useEffect(() => {
    getEmployeeRecommendationBenefits(
      currentPage,
      14,
      orderBy,
      employeeName,
      referencedByName,
      payment,
      benefitStatus,
      month,
      year
    ).then((res) => {
      if (res.status == 200) {
        console.log(res);
        setEmployees(res.data.result);
        setTotalRecords(res.data.totalRecords);
        setLoading(false);
      } else {
        AdnMessageError(t("employeeAwards.messages.couldNotLoadData"));
      }
    });
    fetchDropDownOptions(employeeName, referencedByName);
  }, [
    currentPage,
    orderBy,
    employeeName,
    referencedByName,
    payment,
    benefitStatus,
    month,
    year,
    refetch,
  ]);

  useEffect(async () => {
    await updateEmployeeRecommendationBenefit(recommendationBenefit).then(
      (res) => {
        if (res.status == 200) {
          setRefetch(!refetch);
        } else
          AdnMessageError(t("employeeAwards.messages.couldNotUpdateBenefit"));
      }
    );
    setCancelModalVisible(false);
    setEditModalVisible(false);
  }, [recommendationBenefit]);

  //FUNCTIONS

  const fetchDropDownOptions = async (employeeName, referencedByName) => {
    await getEmployees(1, 14, "", employeeName, "", "", "", "", "").then(
      (res) => {
        if (res.status == 200) {
          setEmployeeList(res.data.result);
        } else
          AdnMessageError(
            t("employeeAwards.messages.errorLoadingEmployeeOptions")
          );
      }
    );
    await getEmployees(1, 14, "", referencedByName, "", "", "", "", "").then(
      (res) => {
        if (res.status == 200) {
          setReferencedByList(res.data.result);
        } else
          AdnMessageError(
            t("employeeAwards.messages.errorLoadingReferencedByOptions")
          );
      }
    );
  };

  const handleCancelFinish = (values) => {
    setRecommendationBenefit({ ...initValues, notes: values.notes, status: 2 });
  };

  const handleEditFinish = (values) => {
    setRecommendationBenefit({ ...initValues, notes: values.notes });
  };

  const handleUnCancelFinish = (values) => {
    setRecommendationBenefit({ ...values, status: 0 });
  };

  const handlePayBenefit = (values) => {
    setRecommendationBenefit({ ...values, status: 1 });
  };

  const handleOrderList = (pag, filter, sorter) => {
    sorter.order === "descend"
      ? setOrderBy("-" + sorter.field)
      : sorter.order === "ascend"
      ? setOrderBy(sorter.field)
      : setOrderBy("");
  };

  const handleSearchEmployee = (value) => {
    value.length >= 2 ? setEmployeeName(value) : setEmployeeName("");
  };

  const handleSelectEmployee = (value) => {
    setEmployeeName(value);
  };

  const handleSearchReferencedBy = (value) => {
    value.length >= 2 ? setReferencedByName(value) : setReferencedByName("");
  };

  const handleSelectReferencedBy = (value) => {
    setReferencedByName(value);
  };

  const handlePaymentChange = (value) => {
    if (value == undefined) return setPayment("");
    setPayment(value);
  };

  const handleStatusChange = (value) => {
    if (value == undefined) return setBenefitStatus("");
    setBenefitStatus(value);
  };

  const handleMonthChange = (value) => {
    if (value == undefined) return setMonth("");
    setMonth(value);
  };

  const handleYearChange = (value) => {
    if (value == undefined) return setYear("");
    setYear(value);
  };

  return (
    <EmployeeAwardsListForm
      title={title}
      loading={loading}
      months={months}
      years={years}
      columns={columns}
      employees={employees}
      totalRecords={totalRecords}
      currentPage={currentPage}
      employeeList={employeeList}
      referencedByList={referencedByList}
      handleOrderList={handleOrderList}
      handleChangePage={setCurrentPage}
      handleSearchEmployee={handleSearchEmployee}
      handleSelectEmployee={handleSelectEmployee}
      handleSearchReferencedBy={handleSearchReferencedBy}
      handleSelectReferencedBy={handleSelectReferencedBy}
      handlePaymentChange={handlePaymentChange}
      handleStatusChange={handleStatusChange}
      handleMonthChange={handleMonthChange}
      handleYearChange={handleYearChange}
      cancelModalVisible={cancelModalVisible}
      setCancelModalVisible={setCancelModalVisible}
      editModalVisible={editModalVisible}
      setEditModalVisible={setEditModalVisible}
      form={form}
      handleCancelFinish={handleCancelFinish}
      handleEditFinish={handleEditFinish}
      initValues={initValues}
    />
  );
};

export default ManageEmployeeAwardsListPage;
